import dayjs from 'dayjs'

export type WeeklyDateRange = {
  from: string
  to: string
}

export type WeekDirection = 'previous' | 'next'

const DATE_FORMAT = 'YYYY-MM-DD'

const parseDate = (value: Date | string) => dayjs(value).startOf('day')

const normalizeBounds = (
  from: Date | string,
  to: Date | string
): WeeklyDateRange | null => {
  const start = parseDate(from)
  const end = parseDate(to)

  if (!start.isValid() || !end.isValid() || start.isAfter(end)) return null

  return {
    from: start.format(DATE_FORMAT),
    to: end.format(DATE_FORMAT),
  }
}

export const getLatestWeekRange = (
  from: Date | string,
  to: Date | string
): WeeklyDateRange | null => {
  const bounds = normalizeBounds(from, to)
  if (!bounds) return null

  const filterStart = parseDate(bounds.from)
  const weekEnd = parseDate(bounds.to)
  const candidateStart = weekEnd.subtract(6, 'day')

  return {
    from: (candidateStart.isBefore(filterStart) ? filterStart : candidateStart).format(
      DATE_FORMAT
    ),
    to: weekEnd.format(DATE_FORMAT),
  }
}

export const shiftWeekRange = (
  current: WeeklyDateRange,
  filterBounds: WeeklyDateRange,
  direction: WeekDirection
): WeeklyDateRange | null => {
  const bounds = normalizeBounds(filterBounds.from, filterBounds.to)
  const normalizedCurrent = normalizeBounds(current.from, current.to)
  if (!bounds || !normalizedCurrent) return null

  const filterStart = parseDate(bounds.from)
  const filterEnd = parseDate(bounds.to)

  if (direction === 'previous') {
    const weekEnd = parseDate(normalizedCurrent.from).subtract(1, 'day')
    if (weekEnd.isBefore(filterStart)) return null

    const candidateStart = weekEnd.subtract(6, 'day')
    return {
      from: (candidateStart.isBefore(filterStart)
        ? filterStart
        : candidateStart
      ).format(DATE_FORMAT),
      to: weekEnd.format(DATE_FORMAT),
    }
  }

  const weekStart = parseDate(normalizedCurrent.to).add(1, 'day')
  if (weekStart.isAfter(filterEnd)) return null

  const candidateEnd = weekStart.add(6, 'day')
  return {
    from: weekStart.format(DATE_FORMAT),
    to: (candidateEnd.isAfter(filterEnd) ? filterEnd : candidateEnd).format(
      DATE_FORMAT
    ),
  }
}

export const formatWeeklyDateRange = (range: WeeklyDateRange) =>
  `${dayjs(range.from).format('DD/MM/YYYY')} - ${dayjs(range.to).format(
    'DD/MM/YYYY'
  )}`
